import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { EmailSchema } from '../../../utils/schema';
import type { ResponseResult } from '@utils';

export type LoginFormValues = {
	email: string;
	password: string;
	error?: string;
};

type Props = {
	onSubmit: (data: LoginFormValues) => Promise<ResponseResult<any>>;
};

export const useLoginForm = ({ onSubmit }: Props) => {
	const [error, setError] = React.useState<string | null>(null);

	const schema = React.useMemo(
		() =>
			z.object({
				email: EmailSchema,
				password: z.string().min(1),
				error: z.any(),
			}),
		[]
	);

	const {
		formState: { isValid, isSubmitted, isSubmitting },
		handleSubmit: internalHandleSubmit,
		control,
		resetField,
	} = useForm<LoginFormValues>({
		defaultValues: {
			email: '',
			password: '',
			error: '',
		},
		resolver: zodResolver(schema),
	});

	const submit = React.useCallback(
		async (data: LoginFormValues): Promise<void> => {
			const { error, result } = await onSubmit(data);

			if (error?.code) {
				setError(error.message);
				resetField('password');

				return;
			}

			setError(null);
		},
		[onSubmit]
	);

	const handleSubmit = React.useMemo(() => internalHandleSubmit(submit), [internalHandleSubmit, submit]);

	return {
		error,
		isSubmitted,
		isValid,
		control,
		handleSubmit,
		isSubmitting,
		setError,
	};
};
